/**
 * Página /me: perfil del usuario autenticado, roles, permisos, cambio de
 * contraseña y placeholder de MFA.
 */
import { useEffect, useState, type FormEvent } from "react";
import { CheckCircle2, Pencil, Shield, ShieldOff, Trash2 } from "lucide-react";
import { useAuth } from "../auth/AuthProvider";
import {
  borrarMiDocumento,
  changePassword,
  miDocumentoUrl,
  subirMiDocumento,
  updateMe,
  type MeResponse,
  type MiPerfilUpdate,
} from "../api/auth";
import { ApiError, formatApiError } from "../api/client";
import { DropZone } from "../components/DropZone";
import { Alert, Badge, Button, Card, CardBody, CardHeader, Field, Input } from "../components/ui";
import { MiFirmaCard } from "../components/MiFirmaCard";
import { TelefonoInput, formatearTelefono } from "../components/TelefonoInput";
import { useZonaHoraria } from "../auth/TenantBrandingProvider";
import { FECHA_MINIMA, edadLegible, hoyISO } from "../lib/edad";
import { fechaHora } from "../lib/fecha";

export function MePage() {
  const { me } = useAuth();

  if (!me) return null;

  // "Contexto: Tenant #253" no le dice nada a nadie: es el nombre de su
  // policlínico lo que la persona reconoce.
  const tenantLabel = me.is_super_admin
    ? "Super-administrador de la plataforma"
    : (me.tenant_nombre ?? "—");

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Mi perfil</h1>
        <p className="text-sm text-slate-500 mt-1">
          Tus datos, tus roles y la seguridad de tu cuenta.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <DatosPersonalesCard me={me} tenantLabel={tenantLabel} />
          {/* Solo a quien firma recetas. Una cuenta de caja o de admisión no
              tiene nada que firmar, y la tarjeta ahí sería un trasto. */}
          {me.profesional_id != null && <MiFirmaCard />}
        </div>

        <Card>
          <CardHeader>
            <h2 className="font-semibold text-slate-900">Autenticación de dos factores</h2>
          </CardHeader>
          <CardBody>
            {me.mfa_habilitado ? (
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-[color:var(--color-link)] mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-slate-900">MFA habilitada</p>
                  <p className="text-xs text-slate-500 mt-1">
                    Tu cuenta requiere código TOTP en cada inicio de sesión.
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-3">
                <ShieldOff className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-slate-900">MFA deshabilitada</p>
                  <p className="text-xs text-slate-500 mt-1">
                    Recomendamos activarla para roles clínicos o administrativos.
                    (UI de enrollment próximamente.)
                  </p>
                </div>
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <h2 className="font-semibold text-slate-900">Roles y permisos</h2>
        </CardHeader>
        <CardBody>
          {me.roles.length === 0 ? (
            <p className="text-sm text-slate-500">Sin roles asignados.</p>
          ) : (
            <div className="space-y-3">
              {me.roles.map((rol) => (
                <div key={rol.id} className="border border-slate-200 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <span className="text-sm font-semibold text-slate-900">{rol.nombre}</span>
                      <span className="ml-2 text-xs text-slate-400 font-mono">{rol.codigo}</span>
                    </div>
                    <span className="text-xs text-slate-500">
                      {rol.permisos.length} permisos
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {rol.permisos.slice(0, 12).map((p) => (
                      <span
                        key={p.codigo}
                        className="text-xs font-mono bg-slate-100 text-slate-700 rounded px-2 py-0.5"
                      >
                        {p.codigo}
                      </span>
                    ))}
                    {rol.permisos.length > 12 && (
                      <span className="text-xs text-slate-400 px-2 py-0.5">
                        +{rol.permisos.length - 12} más
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      <ChangePasswordCard />
    </div>
  );
}

const SEXOS = [
  { value: "M", label: "Masculino" },
  { value: "F", label: "Femenino" },
  { value: "O", label: "Otro" },
];

/**
 * Datos de la cuenta y de la persona, editables por su dueño.
 *
 * El email y los roles no se editan aquí a propósito: el email es con lo que
 * se inicia sesión y los roles son decisión de quien administra. Eso vive en
 * la gestión de usuarios.
 */
function DatosPersonalesCard({
  me,
  tenantLabel,
}: {
  me: MeResponse;
  tenantLabel: string;
}) {
  const tz = useZonaHoraria();
  const { refreshMe } = useAuth();
  const [editando, setEditando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);
  const [form, setForm] = useState<MiPerfilUpdate>({});

  // Al abrir la edición, partimos de lo que ya hay guardado.
  useEffect(() => {
    if (!editando) return;
    setForm({
      tipo_documento: me.persona?.tipo_documento ?? "DNI",
      num_documento: me.persona?.num_documento ?? "",
      nombres: me.persona?.nombres ?? "",
      ap_paterno: me.persona?.ap_paterno ?? "",
      ap_materno: me.persona?.ap_materno ?? "",
      fecha_nacimiento: me.persona?.fecha_nacimiento ?? "",
      sexo: me.persona?.sexo ?? "",
      telefono: me.persona?.telefono ?? "",
    });
    setError(null);
    setOk(false);
  }, [editando, me]);

  async function guardar(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setGuardando(true);
    try {
      // Los vacíos van como null, no como "": una cadena vacía en la base es
      // un dato que parece existir y no existe.
      const limpio: MiPerfilUpdate = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, v === "" ? null : v]),
      );
      await updateMe(limpio);
      await refreshMe();
      setEditando(false);
      setOk(true);
    } catch (err) {
      setError(formatApiError(err, "No se pudo guardar."));
    } finally {
      setGuardando(false);
    }
  }

  if (!editando) {
    const p = me.persona;
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <h2 className="font-semibold text-slate-900">Mis datos</h2>
            {!p && <Badge variant="warning">Sin completar</Badge>}
            <Button
              variant="ghost"
              className="ml-auto"
              onClick={() => setEditando(true)}
            >
              <Pencil className="h-4 w-4" />
              Editar
            </Button>
          </div>
        </CardHeader>
        <CardBody className="space-y-3 text-sm">
          {ok && <Alert variant="success">Datos guardados.</Alert>}
          {!p && (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
              Todavía no registraste tus datos personales. Complétalos para que tu
              nombre aparezca en la historia clínica y en lo que firmes.
            </p>
          )}
          <Row label="Nombre" value={p?.nombre_formal ?? "—"} />
          <Row label="Nombres" value={p?.nombres ?? "—"} />
          <Row
            label="Apellidos"
            value={p ? `${p.ap_paterno} ${p.ap_materno ?? ""}`.trim() : "—"}
          />
          <Row
            label="Documento"
            value={p ? `${p.tipo_documento} ${p.num_documento}` : "—"}
          />
          <Row label="Fecha de nacimiento" value={p?.fecha_nacimiento ?? "—"} />
          <Row
            label="Sexo"
            value={SEXOS.find((s) => s.value === p?.sexo)?.label ?? "—"}
          />
          <Row label="Teléfono" value={formatearTelefono(p?.telefono)} />
          <div className="border-t border-slate-100 pt-3">
            <Row label="Email (con el que inicias sesión)" value={me.email} />
            <Row label="Policlínico" value={tenantLabel} />
            <Row label="Estado" value={me.activo ? "Activo" : "Inactivo"} />
            <Row
              label="Último ingreso"
              value={me.ultimo_login ? fechaHora(me.ultimo_login, tz) : "—"}
            />
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <h2 className="font-semibold text-slate-900">Editar mis datos</h2>
      </CardHeader>
      <CardBody>
        <form onSubmit={guardar} className="space-y-4">
          {error && <Alert>{error}</Alert>}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Field label="Tipo de documento" htmlFor="p_tipo">
              <select
                id="p_tipo"
                value={form.tipo_documento ?? "DNI"}
                onChange={(e) =>
                  setForm({ ...form, tipo_documento: e.target.value })
                }
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              >
                <option value="DNI">DNI</option>
                <option value="CE">Carné de extranjería</option>
                <option value="PAS">Pasaporte</option>
              </select>
            </Field>
            <Field label="Número *" htmlFor="p_doc">
              <Input
                id="p_doc"
                value={form.num_documento ?? ""}
                onChange={(e) =>
                  setForm({ ...form, num_documento: e.target.value })
                }
                maxLength={20}
                className="font-mono"
                required
              />
            </Field>
            <Field label="Teléfono" htmlFor="p_tel">
              <TelefonoInput
                id="p_tel"
                value={form.telefono}
                onChange={(v) => setForm({ ...form, telefono: v })}
              />
            </Field>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Field label="Nombres *" htmlFor="p_nom">
              <Input
                id="p_nom"
                value={form.nombres ?? ""}
                onChange={(e) => setForm({ ...form, nombres: e.target.value })}
                maxLength={200}
                required
              />
            </Field>
            <Field label="Apellido paterno *" htmlFor="p_apa">
              <Input
                id="p_apa"
                value={form.ap_paterno ?? ""}
                onChange={(e) => setForm({ ...form, ap_paterno: e.target.value })}
                maxLength={100}
                required
              />
            </Field>
            <Field label="Apellido materno" htmlFor="p_apm">
              <Input
                id="p_apm"
                value={form.ap_materno ?? ""}
                onChange={(e) => setForm({ ...form, ap_materno: e.target.value })}
                maxLength={100}
              />
            </Field>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field
              label="Fecha de nacimiento"
              htmlFor="p_fnac"
              hint={edadLegible(form.fecha_nacimiento) ?? undefined}
            >
              <Input
                id="p_fnac"
                type="date"
                value={form.fecha_nacimiento ?? ""}
                max={hoyISO()}
                min={FECHA_MINIMA}
                onChange={(e) =>
                  setForm({ ...form, fecha_nacimiento: e.target.value })
                }
              />
            </Field>
            <Field label="Sexo" htmlFor="p_sexo">
              <select
                id="p_sexo"
                value={form.sexo ?? ""}
                onChange={(e) => setForm({ ...form, sexo: e.target.value })}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              >
                <option value="">— sin especificar —</option>
                {SEXOS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <div className="border-t border-slate-100 pt-4">
            <p className="mb-2 text-sm font-medium text-slate-700">
              Foto del documento{" "}
              <span className="text-xs font-normal text-slate-400">(opcional)</span>
            </p>
            <DocumentoDropZone me={me} />
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-slate-100 pt-3">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setEditando(false)}
            >
              Cancelar
            </Button>
            <Button type="submit" loading={guardando}>
              Guardar
            </Button>
          </div>
        </form>
      </CardBody>
    </Card>
  );
}

/**
 * Escaneo del documento de identidad, dentro del formulario de datos.
 *
 * Sube al soltarlo, sin esperar a "Guardar": es un archivo, no un campo de
 * texto, y hacerle pulsar un botón después de arrastrar es un paso de más.
 * Lo sirve el backend con la sesión, nunca por una URL pública.
 */
function DocumentoDropZone({ me }: { me: MeResponse }) {
  const { refreshMe } = useAuth();
  const [subiendo, setSubiendo] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function subir(f: File | null) {
    if (!f) return;
    setError(null);
    setSubiendo(true);
    try {
      await subirMiDocumento(f);
      await refreshMe();
    } catch (err) {
      setError(formatApiError(err, "No se pudo subir el documento."));
    } finally {
      setSubiendo(false);
    }
  }

  async function borrar() {
    setError(null);
    try {
      await borrarMiDocumento();
      await refreshMe();
    } catch (err) {
      setError(formatApiError(err, "No se pudo quitar el documento."));
    }
  }

  if (!me.persona) {
    return (
      <p className="text-sm text-slate-400">
        Guarda primero tus datos y podrás adjuntar la foto de tu documento.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {error && <Alert>{error}</Alert>}
      {me.persona.has_documento ? (
        <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
          <img
            src={miDocumentoUrl()}
            alt="Documento de identidad"
            className="h-16 w-24 shrink-0 rounded object-cover"
            onError={(e) => {
              // Si es PDF no hay miniatura; no dejamos el icono roto.
              (e.currentTarget as HTMLImageElement).style.display = "none";
            }}
          />
          <p className="flex-1 text-sm text-slate-700">
            {me.persona.tipo_documento} {me.persona.num_documento} — archivo cargado.
          </p>
          <Button type="button" variant="danger" onClick={borrar}>
            <Trash2 className="h-4 w-4" />
            Quitar
          </Button>
        </div>
      ) : (
        <DropZone
          id="me_doc"
          file={null}
          onFile={subir}
          hint={subiendo ? "Subiendo…" : "PNG, JPG o PDF · hasta 5 MB"}
        />
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="text-slate-500">{label}</span>
      <span className="text-right font-medium text-slate-900">{value ?? "—"}</span>
    </div>
  );
}

function ChangePasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (next.length < 8) {
      setError("La nueva contraseña debe tener al menos 8 caracteres.");
      return;
    }
    if (next !== confirm) {
      setError("La confirmación no coincide.");
      return;
    }

    setSubmitting(true);
    try {
      await changePassword({ current_password: current, new_password: next });
      setSuccess(true);
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        const detail = (err.payload as { detail?: string })?.detail;
        setError(detail ?? "No se pudo cambiar la contraseña.");
      } else {
        setError("Error de conexión. Intenta de nuevo.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <h2 className="font-semibold text-slate-900">Cambiar contraseña</h2>
      </CardHeader>
      <CardBody>
        {success && (
          <div className="mb-4">
            <Alert variant="success">
              <CheckCircle2 className="inline w-4 h-4 mr-1" />
              Contraseña actualizada correctamente.
            </Alert>
          </div>
        )}
        {error && (
          <div className="mb-4">
            <Alert variant="error">{error}</Alert>
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4 max-w-md">
          <Field label="Contraseña actual" htmlFor="current_password">
            <Input
              id="current_password"
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
            />
          </Field>
          <Field label="Nueva contraseña" htmlFor="new_password" hint="Mínimo 8 caracteres.">
            <Input
              id="new_password"
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              required
            />
          </Field>
          <Field label="Confirmar nueva contraseña" htmlFor="confirm_password">
            <Input
              id="confirm_password"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
            />
          </Field>
          <Button type="submit" loading={submitting}>
            Actualizar contraseña
          </Button>
        </form>
      </CardBody>
    </Card>
  );
}
